// API Base URL - automatically detects environment

// const API_BASE_URL='http://localhost:8000/api';
const API_BASE_URL='https://bookingapi.nazaistudio.ma/api';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token_nazaibooking');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erreur de connexion' }));

    // Extract the most relevant error message
    let errorMessage = 'Une erreur est survenue';

    if (error.error) {
      // Single error message from backend
      errorMessage = error.error;
    } else if (error.non_field_errors && error.non_field_errors.length > 0) {
      // Non-field errors (validation errors)
      errorMessage = error.non_field_errors[0];
    } else if (error.detail) {
      // Detail error message
      errorMessage = error.detail;
    } else if (error.message) {
      // Generic message
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    } else {
      // Fallback based on status code
      switch (response.status) {
        case 400:
          errorMessage = 'Données invalides. Veuillez vérifier vos informations.';
          break;
        case 401:
          errorMessage = 'Vous devez être connecté pour effectuer cette action.';
          break;
        case 403:
          errorMessage = 'Vous n\'avez pas l\'autorisation d\'effectuer cette action.';
          break;
        case 404:
          errorMessage = 'Ressource introuvable.';
          break;
        case 500:
          errorMessage = 'Erreur serveur. Veuillez réessayer plus tard.';
          break;
        default:
          errorMessage = `Erreur ${response.status}. Veuillez réessayer.`;
      }
    }

    throw new Error(errorMessage);
  }
  return response.json();
};

// Authentication API
export const authAPI = {
  // Register new user
  register: async (userData) => {
    const response = await fetch(`${API_BASE_URL}/auth/register/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });
    const data = await handleResponse(response);
    
    // Store tokens
    if (data.access) {
      localStorage.setItem('access_token_nazaibooking', data.access);
      localStorage.setItem('refresh_token', data.refresh);
    }
    
    return data;
  },

  // Login user
  login: async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/login/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = await handleResponse(response);
    
    // Store tokens
    if (data.access) {
      localStorage.setItem('access_token_nazaibooking', data.access);
      localStorage.setItem('refresh_token', data.refresh);
    }
    
    return data;
  },

  // Get current user profile
  getProfile: async () => {
    const response = await fetch(`${API_BASE_URL}/auth/profile/`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Update user profile
  updateProfile: async (userData) => {
    const response = await fetch(`${API_BASE_URL}/auth/profile/update/`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData)
    });
    return handleResponse(response);
  },

  // Refresh token
  refreshToken: async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${API_BASE_URL}/auth/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: refreshToken })
    });
    const data = await handleResponse(response);
    
    if (data.access) {
      localStorage.setItem('access_token_nazaibooking', data.access);
    }
    
    return data;
  },

  // Logout
  logout: () => {
    localStorage.removeItem('access_token_nazaibooking');
    localStorage.removeItem('refresh_token');
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    return !!localStorage.getItem('access_token_nazaibooking');
  },

  // Password Reset
  sendPasswordResetCode: async (email) => {
    const response = await fetch(`${API_BASE_URL}/auth/send-password-reset-code/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    return handleResponse(response);
  },

  verifyPasswordResetCode: async (email, code) => {
    const response = await fetch(`${API_BASE_URL}/auth/verify-password-reset-code/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code })
    });
    return handleResponse(response);
  },

  resetPassword: async (email, code, newPassword) => {
    const response = await fetch(`${API_BASE_URL}/auth/reset-password/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code, new_password: newPassword })
    });
    return handleResponse(response);
  }
};

// Public data API
export const publicAPI = {
  // Get all locations
  getLocations: async () => {
    const response = await fetch(`${API_BASE_URL}/locations/`);
    return handleResponse(response);
  },

  // Get all coaches
  getCoaches: async () => {
    const response = await fetch(`${API_BASE_URL}/coaches/`);
    return handleResponse(response);
  },

  // Get all service types (all in one if backend has no pagination, else fetch all pages)
  getServices: async () => {
    const response = await fetch(`${API_BASE_URL}/services/`);
    const data = await handleResponse(response);
    // Backend may return a plain list (no pagination) or { results, next }
    const list = data?.results ?? data;
    const all = Array.isArray(list) ? [...list] : [];
    let nextUrl = data?.next;
    while (nextUrl) {
      const nextRes = await fetch(nextUrl);
      const nextData = await nextRes.json();
      const nextList = nextData?.results ?? [];
      if (Array.isArray(nextList)) all.push(...nextList);
      nextUrl = nextData?.next;
    }
    return all;
  },

  // Get sessions with filters
  getSessions: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.date) params.append('date', filters.date);
    if (filters.week) params.append('week', filters.week);
    if (filters.week_start) params.append('week_start', filters.week_start);
    if (filters.coach_id) params.append('coach_id', filters.coach_id);
    if (filters.location_id) params.append('location_id', filters.location_id);
    if (filters.status) params.append('status', filters.status);

    // Always get all sessions without pagination (since we disabled pagination on backend)
    // This ensures we get all sessions for the time slot
    const url = `${API_BASE_URL}/sessions/${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url);
    return handleResponse(response);
  },

  // Get session details
  getSessionDetail: async (sessionId) => {
    const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/`);
    return handleResponse(response);
  }
};

// Booking API
export const bookingAPI = {
  // Create a new booking
  createBooking: async (sessionId, clientServiceId) => {
    const response = await fetch(`${API_BASE_URL}/bookings/create/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        session_id: sessionId,
        client_service_id: clientServiceId
      })
    });
    return handleResponse(response);
  },

  // Cancel a booking
  cancelBooking: async (bookingId, reason = '') => {
    const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}/cancel/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason })
    });
    return handleResponse(response);
  },

  // Get user's bookings
  getMyBookings: async (page = 1) => {
    const response = await fetch(`${API_BASE_URL}/bookings/my/?page=${page}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Get user's waitlist entries
  getMyWaitlist: async () => {
    const response = await fetch(`${API_BASE_URL}/waitlist/my/`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Get user's client services
  getMyClientServices: async () => {
    const response = await fetch(`${API_BASE_URL}/client-services/my/`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Get user's active client services
  getMyActiveClientServices: async () => {
    const response = await fetch(`${API_BASE_URL}/client-services/my/active/`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Get user's pending client services (awaiting payment confirmation)
  getMyPendingClientServices: async () => {
    const response = await fetch(`${API_BASE_URL}/client-services/my/pending/`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Create a new client service (purchase service)
  createClientService: async (serviceData) => {
    const response = await fetch(`${API_BASE_URL}/client-services/create/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(serviceData)
    });
    return handleResponse(response);
  },

  // Join waitlist
  joinWaitlist: async (sessionId, clientServiceId) => {
    const response = await fetch(`${API_BASE_URL}/waitlist/join/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        session_id: sessionId,
        client_service_id: clientServiceId
      })
    });
    return handleResponse(response);
  },

  // Get notifications
  getNotifications: async (page = 1) => {
    const response = await fetch(`${API_BASE_URL}/notifications/?page=${page}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Mark notification as read
  markNotificationRead: async (notificationId) => {
    const response = await fetch(`${API_BASE_URL}/notifications/${notificationId}/read/`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Mark all notifications as read
  markAllNotificationsRead: async () => {
    const response = await fetch(`${API_BASE_URL}/notifications/mark-all-read/`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Mark all notifications as read
  markAllNotificationsRead: async () => {
    const response = await fetch(`${API_BASE_URL}/notifications/mark-all-read/`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

// Admin API (for coaches and admins)
export const adminAPI = {
  // Create recurring sessions
  createRecurringSessions: async (sessionData) => {
    const response = await fetch(`${API_BASE_URL}/admin/recurring-sessions/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(sessionData)
    });
    return handleResponse(response);
  }
};

// Error handling for expired tokens
export const setupAxiosInterceptors = () => {
  // This would be used with axios, but we're using fetch
  // We can implement token refresh logic in our API calls
};

export default {
  authAPI,
  publicAPI,
  bookingAPI,
  adminAPI
};
